<script setup lang="ts">
import { useUserStore } from '@core/stores/user'
import PlayerLastGamesEffectiveness from '@/views/dashboards/PlayerLastGamesEffectiveness.vue'
import PlayerSummary from '@/views/dashboards/PlayerSummary.vue'

interface Subordinate {
  id: string
  name: string
  last_name: string
  effectiveness: number
  has_avatar: boolean
}

definePage({
  meta: {
    action: 'read',
    subject: 'dashboard-head',
  },
})

const { t } = useI18n()
const userStore = useUserStore()

const subordinates = ref<Subordinate[]>([])
const subordinatesLoading = ref<boolean>(false)
const selectedSubordinateId = ref<string | undefined>()

const selectedSubordinate = computed(() =>
  subordinates.value.find(s => s.id === selectedSubordinateId.value),
)

const fetchSubordinates = async () => {
  subordinatesLoading.value = true

  const { data } = await useApi<Subordinate[]>('/users/my-subordinates')
  if (data?.value)
    subordinates.value = data.value

  subordinatesLoading.value = false
}

fetchSubordinates()

const selectSubordinate = (id: string) => {
  selectedSubordinateId.value = selectedSubordinateId.value === id ? undefined : id
}
</script>

<template>
  <div>
    <!-- Personal stats section -->
    <VRow class="pt-6">
      <VCol
        cols="12"
        md="8"
        class="match-height"
      >
        <div class="pe-3">
          <h5 class="text-h5 mb-2">
            {{ t('dashboards.player.summary.welcome') }}, <span class="text-h4"> {{ userStore.fullName.split(' ')[0] }} </span>
          </h5>

          <div
            class="text-wrap text-body-1"
            style="max-inline-size: 360px;"
          >
            {{ t('dashboards.player.summary.progress') }}:
          </div>

          <div class="d-flex justify-space-between flex-wrap gap-4 flex-column flex-sm-row mt-10">
            <PlayerSummary />
          </div>
        </div>
      </VCol>

      <VCol
        cols="12"
        md="4"
      >
        <PlayerLastGamesEffectiveness />
      </VCol>
    </VRow>

    <!-- Subordinates section -->
    <VRow class="pt-6">
      <VCol cols="12">
        <VCard>
          <VCardItem :title="t('dashboards.head.subordinates')" />
          <VCardText>
            <div
              v-if="subordinatesLoading"
              class="d-flex justify-center py-4"
            >
              <VProgressCircular indeterminate />
            </div>

            <div
              v-else-if="subordinates.length === 0"
              class="text-body-1 text-medium-emphasis text-center py-4"
            >
              {{ t('dashboards.head.no_subordinates') }}
            </div>

            <VRow v-else>
              <VCol
                v-for="subordinate in subordinates"
                :key="subordinate.id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <VCard
                  :variant="selectedSubordinateId === subordinate.id ? 'tonal' : 'outlined'"
                  :color="selectedSubordinateId === subordinate.id ? 'primary' : undefined"
                  class="cursor-pointer"
                  @click="selectSubordinate(subordinate.id)"
                >
                  <VCardText class="d-flex align-center gap-3">
                    <VAvatar
                      size="40"
                      :variant="!subordinate.has_avatar ? 'tonal' : undefined"
                      color="primary"
                    >
                      <VImg
                        v-if="subordinate.has_avatar"
                        :src="`/api/account/avatar-${subordinate.id}`"
                      />
                      <span v-else>{{ avatarText(`${subordinate.name} ${subordinate.last_name}`) }}</span>
                    </VAvatar>
                    <div class="flex-grow-1">
                      <h6 class="text-h6">
                        {{ subordinate.name }} {{ subordinate.last_name }}
                      </h6>
                      <span class="text-body-2 text-medium-emphasis">
                        {{ t('dashboards.player.summary.stats.effectiveness') }}: {{ subordinate.effectiveness }}
                      </span>
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Selected subordinate stats -->
    <template v-if="selectedSubordinate">
      <VRow class="pt-6">
        <VCol cols="12">
          <h5 class="text-h5">
            {{ t('dashboards.head.subordinate_stats') }}: {{ selectedSubordinate.name }} {{ selectedSubordinate.last_name }}
          </h5>
        </VCol>
      </VRow>

      <VRow>
        <VCol
          cols="12"
          md="8"
          class="match-height"
        >
          <div class="d-flex justify-space-between flex-wrap gap-4 flex-column flex-sm-row">
            <PlayerSummary :player-id="selectedSubordinateId" />
          </div>
        </VCol>

        <VCol
          cols="12"
          md="4"
        >
          <PlayerLastGamesEffectiveness :player-id="selectedSubordinateId" />
        </VCol>
      </VRow>
    </template>

    <div
      v-else-if="subordinates.length > 0"
      class="pt-6"
    >
      <VCard variant="tonal">
        <VCardText class="text-center text-body-1 text-medium-emphasis py-8">
          {{ t('dashboards.head.select_subordinate') }}
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/libs/apex-chart.scss";
</style>
